import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Badge, Container, Spinner } from "reactstrap";
import { formatDateTime } from "../../../util/formatters";
import { useCallback, useEffect, useState } from "react";
import { CircleCheckBig, CircleX } from "lucide-react";
import { adminApi as api } from "../../../util/api";
import { PaginationComponent } from "../../../components/inputs/PaginationComponent";
import { AnimatePresence, motion } from "framer-motion";
import { DetailedErrorAlert } from "../../../components/DetailedErrorAlert";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const parseStatusCodeColor = (code) => {
  switch (true) {
    case code >= 100 && code <= 199:
      return "info";
    case code >= 200 && code <= 299:
      return "success";
    case code >= 300 && code <= 399:
      return "info";
    case code >= 400 && code <= 599:
      return "danger";
    default:
      return "info";
  }
};

const Activity = ({ record, index }) => {
  return (
    <AccordionItem>
      <AccordionHeader targetId={`${index + 1}`}>
        <p className="mb-0">UUID: {record.request_uuid}</p>

        <p className="mx-4 mb-0">
          Method: <Badge>{record.request_method}</Badge>
        </p>

        <p className="me-4 mb-0">
          Status: <Badge color={parseStatusCodeColor(record.response_status)}>{record.response_status}</Badge>
        </p>

        <p className="me-4 mb-0">Identity: {record.identity}</p>

        <p className="mb-0">Timestamp: {formatDateTime(record.created_at)}</p>
      </AccordionHeader>

      <AccordionBody accordionId={`${index + 1}`}>
        <div className="bg-secondary rounded-2 p-4">
          <p className="text-white">
            <strong>Idempotency Key</strong>: {record.idempotency_key}
          </p>

          <p className="text-white">
            <strong>Context</strong>: {record.context}
          </p>

          <p className="text-white">
            <strong>User Agent</strong>: {record.user_agent}
          </p>

          <p className="text-white">
            <strong>Referrer</strong>: {record.referrer || "None"}
          </p>

          <p className="text-white">
            <strong>IP</strong>: {record.ip}
          </p>

          <p className="text-white">
            <strong>Status</strong>: <Badge color={parseStatusCodeColor(record.response_status)}>{record.response_status}</Badge>
          </p>

          <p className="text-white">
            <strong>Success</strong>: {record.success ? <CircleCheckBig color="green" /> : <CircleX color="red" />}
          </p>

          <p className="text-white">
            <strong>Request Body</strong>:{" "}
            {record.request_body_url ? (
              <>
                <a className="text-link" href={record.request_body_url} target="_blank" rel="noopener noreferrer" download>
                  Download JSON
                </a>
              </>
            ) : (
              <small>Not Attached</small>
            )}
          </p>

          <p className="text-white">
            <strong>Response Body</strong>:{" "}
            {record.response_body_url ? (
              <>
                <a className="text-link" href={record.response_body_url} target="_blank" rel="noopener noreferrer" download>
                  Download JSON
                </a>
              </>
            ) : (
              <small>Not Attached</small>
            )}
          </p>
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

const ActivityList = ({ records }) => {
  const [open, setOpen] = useState("1");

  const toggle = (id) => {
    if (open === id) {
      setOpen();
    } else {
      setOpen(id);
    }
  };

  return (
    <Accordion className="mt-3" open={open} toggle={toggle}>
      {records.map((record, index) => {
        return <Activity record={record} index={index} key={`request-${index + 1}`} />;
      })}
    </Accordion>
  );
};

export const AuthTokenApiActivitiesTab = ({ _authToken, authTokenId, _refresh }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (currentPage, pageSize) => {
      try {
        setLoading(true);
        const params = { page: currentPage, per: pageSize };

        const req = await api.get(`/admin/auth_tokens/${authTokenId}/api_activities.json`, { params });
        const { meta, resources } = req.data;

        setTotalPages(meta?.total_pages);
        setResults(resources || []);

        setLoading(false);
      } catch (err) {
        setError(err);
        console.error(err);
      }
    },
    [authTokenId]
  );

  useEffect(() => {
    if (!results) {
      fetchData(page, pageSize);
    }
  }, [fetchData, page, pageSize, results]);

  const handlePageChange = async (args) => {
    const newPage = args + 1;
    setPage(() => newPage);

    await fetchData(newPage, pageSize);
  };

  const handlePageSizeChange = async (args) => {
    await setPageSize(() => args);
    await fetchData(page, args);
  };

  return (
    <>
      <Container>
        <div className="bg-info bg-opacity-10 p-3 rounded-2 mb-3">
          <h4>Hint:</h4>
          <p className="mb-0">All previous requests will be listed below.</p>
        </div>

        {error && <DetailedErrorAlert error={error} />}

        <AnimatePresence mode="sync">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "spring", delay: 1, stiffness: 50 }}
          >
            {loading ? (
              <div className="d-flex flex-row justify-content-center align-items-center w-100">
                <Spinner color="secondary">Loading</Spinner>
              </div>
            ) : (
              <>
                {results && results.length > 0 ? (
                  <>
                    <PaginationComponent
                      className="mb-2"
                      page={page - 1}
                      pages={totalPages}
                      showPageSizeOptions
                      showPageJump
                      pageSize={pageSize}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      loading={loading}
                      canPrevious={page > 1}
                      canNext={page < totalPages}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />

                    <ActivityList records={results} />
                  </>
                ) : (
                  <div
                    className="d-flex flex-row justify-content-center align-items-center border rounded-2"
                    style={{ minHeight: "12rem" }}
                  >
                    <div className="d-flex flex-row bg-light rounded p-3 justify-content-center gap-1 align-items-center">
                      <p className="lead fw-normal mb-0">No requests for now...</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </Container>
    </>
  );
};
